// WP0 Task 6 — the single-source FAQ store (spec §10): ONE file of
// { id, question, answer, surfaces } entries feeding marketing /faq, the
// homepage FAQ and the panel help slide-over. Price/limit figures inside
// answers are {tokens} interpolated from PLANS-derived figures — repricing
// updates the FAQ automatically; a hand-written dollar amount in `answer`
// is a test failure.
//
// WP6 completed the migration 2026-08-16: 38 legacy answers → 29 deduped
// entries (ledger in docs/superpowers/plans/2026-08-16-wp6-faq-help.md); the
// legacy marketing datasets are deleted by WP4 when its pages switch to this
// store.
//
// Leaf charter: no runtime imports. fmtUsd duplicates pricing-display's
// formatUsd (escrow.ts/capacityFor precedent); the faq-store test pins the
// two via the shared figures assertion.

// 'marketing_pricing' added WP6 (CONSOLIDATION v2 §A4): the 7 pricing-group
// FAQ entries land tagged with this surface in T2-4 — this task only widens
// the union so the type exists ahead of content.
export type FaqSurface = 'marketing_home' | 'marketing_faq' | 'marketing_pricing' | 'app_help';

export interface FaqEntry {
  id: string;
  /** Grouping for the /faq page ("Getting started", "Google & AI", …). */
  group: string;
  question: string;
  /** May contain {figureToken}s — see buildFaqFigures. NEVER literal figures. */
  answer: string;
  surfaces: readonly FaqSurface[];
  /** Panel screen ids this entry is relevant to — the "?" slide-over shows
   *  the 3-5 entries whose screens include the current screen (spec §10). */
  screens?: readonly string[];
}

export interface FaqFigureSource {
  plans: Record<
    'pro' | 'business' | 'growth',
    {
      label: string;
      monthlyPriceCents: number;
      annualPriceCents: number;      // WP6: {starterAnnual}
      aiGenerationsMonthly: number;  // WP6: {aiHelpsBusiness}/{aiHelpsGrowth}
      locationsIncluded: number;
    }
  >;
  units: Record<
    'pro' | 'business' | 'growth',
    { searchTerms: number; competitors: number; aiQuestions: number }
  >;
  locationAddon: { priceCents: number; extraSearchTerms: number; extraCompetitors: number };
  searchTermAddon: { termsPerPack: number; priceCents: number; maxPacks: number };
  aiPack: { generations: number; priceCents: number };
  concierge: { polishPriceCents: number; fullPriceCents: number }; // WP6
  supportEmail: string;                                            // WP6
  demoTtlDays: number;
  moneyBackDays: number;
}

function fmtUsd(cents: number): string {
  const dollars = cents / 100;
  return Number.isInteger(dollars)
    ? `$${dollars.toLocaleString('en-US')}`
    : `$${dollars.toFixed(2)}`;
}

export function buildFaqFigures(src: FaqFigureSource): Readonly<Record<string, string>> {
  return {
    starterPlanName: src.plans.pro.label, // locked #20: internal `pro` displays "Starter"
    businessPlanName: src.plans.business.label,
    growthPlanName: src.plans.growth.label,
    starterMonthly: fmtUsd(src.plans.pro.monthlyPriceCents),
    businessMonthly: fmtUsd(src.plans.business.monthlyPriceCents),
    growthMonthly: fmtUsd(src.plans.growth.monthlyPriceCents),
    growthLocationsIncluded: String(src.plans.growth.locationsIncluded),
    locationAddonMonthly: fmtUsd(src.locationAddon.priceCents),
    locationExtraSearchTerms: String(src.locationAddon.extraSearchTerms),
    locationExtraCompetitors: String(src.locationAddon.extraCompetitors),
    searchTermPackPrice: fmtUsd(src.searchTermAddon.priceCents),
    searchTermPackSize: String(src.searchTermAddon.termsPerPack),
    searchTermPackMax: String(src.searchTermAddon.maxPacks),
    aiPackPrice: fmtUsd(src.aiPack.priceCents),
    aiPackSize: String(src.aiPack.generations),
    proSearchTerms: String(src.units.pro.searchTerms),
    proCompetitors: String(src.units.pro.competitors),
    proAiQuestions: String(src.units.pro.aiQuestions),
    starterAnnual: fmtUsd(src.plans.pro.annualPriceCents),
    aiHelpsBusiness: String(src.plans.business.aiGenerationsMonthly),
    aiHelpsGrowth: String(src.plans.growth.aiGenerationsMonthly),
    conciergePolishPrice: fmtUsd(src.concierge.polishPriceCents),
    conciergeFullPrice: fmtUsd(src.concierge.fullPriceCents),
    supportEmail: src.supportEmail,
    demoTtlDays: String(src.demoTtlDays),
    moneyBackDays: String(src.moneyBackDays),
  };
}

export function renderFaqAnswer(
  entry: FaqEntry,
  figures: Readonly<Record<string, string>>,
): string {
  return entry.answer.replace(/\{([A-Za-z0-9_]+)\}/g, (_m, key: string) => {
    const value = figures[key];
    if (value === undefined) {
      throw new Error(`FAQ entry "${entry.id}" references unknown figure {${key}}`);
    }
    return value;
  });
}

export function faqForSurface(
  entries: readonly FaqEntry[],
  surface: FaqSurface,
): FaqEntry[] {
  return entries.filter((e) => e.surfaces.includes(surface));
}

// ---------------------------------------------------------------------------
// WP0 slice - 7 entries migrated from apps/marketing/src/lib/content.ts
// (HOME_FAQ / FAQ_GROUPS), answers verbatim except figures -> tokens.
// ---------------------------------------------------------------------------
export const FAQ_ENTRIES: readonly FaqEntry[] = [
  // ── Getting started ────────────────────────────────────────────────────
  {
    id: 'not-technical',
    group: 'Getting started',
    question: "I'm not technical — can I still use this?",
    // WP6 merge (Conflict 2): HOME_FAQ + FAQ_GROUPS asked this twice with
    // different answers; the HOME_FAQ variant carried stale "as Google access
    // rolls out" copy (GBP shipped 2026-08-02). FAQ_GROUPS wording survives.
    answer:
      'no. if you can send a text, you can use pageboss. you type your business name, we build a starting site from your business info (and your Google listing if you have one), and you change the wording or swap photos in plain English — no design skills, no code, ever.',
    surfaces: ['marketing_home', 'marketing_faq'],
  },
  {
    id: 'what-is-google-profile',
    group: 'Getting started',
    // Glossary-adjusted question (Conflict 3): binding noun is "Google Profile".
    question: "what's a Google Profile, and do I have one?",
    answer:
      "it's the listing that shows your hours, map pin, photos, and reviews when someone Googles you. if your business comes up on Google or Maps, you already have one. either way pageboss builds your site from your business info — and if you don't have a listing yet, we help you get set up so customers can find you.",
    surfaces: ['marketing_faq', 'app_help'],
    screens: ['gbp'],
  },
  {
    id: 'what-we-build',
    group: 'Getting started',
    question: 'what do you actually build when I type my name?',
    answer:
      'we take your business info (your name, what you do, and your Google listing if you have one) and assemble a clean starting site with your hours, services, and photos. not a blank page. you preview it, then make it yours.',
    surfaces: ['marketing_faq'],
  },
  {
    id: 'how-long',
    group: 'Getting started',
    question: 'how long does it take to get up?',
    answer:
      'your starting site appears in about a minute. most owners are happy after a few small edits and go live the same afternoon.',
    surfaces: ['marketing_faq'],
  },
  {
    id: 'who-its-for',
    group: 'Getting started',
    question: 'is this for me?',
    answer:
      "pageboss is built for local service businesses where your customers are your neighbors — the plumber, the mobile caterer, the daycare, the detailer. no website skills needed and no agency-sized budget. if you're an online-only brand, we're probably not your tool.",
    surfaces: ['marketing_home', 'marketing_faq'],
  },
  {
    id: 'concierge',
    group: 'Getting started',
    question: 'what is “we’ll build it for you” — the concierge / done-for-you option?',
    // WP6 honesty fix (Conflict 1): the legacy answer sold a "concierge add-on
    // at checkout" that does not exist. Only the real path (email) survives.
    answer:
      "if you'd rather hand it off, our concierge does the setup work for you on top of your plan: {conciergePolishPrice} one-time gets your starting site polished and ready, and we launch it with you once you pick your plan; {conciergeFullPrice} is the full white-glove setup (multiple pages, your domain connected, photos and copy dialed in). either way you own everything: your site, your content, your domain, yours to keep or edit anytime. to start, email us at {supportEmail} and we'll take it from there.",
    surfaces: ['marketing_faq'],
  },
  // ── Demo & claiming ────────────────────────────────────────────────────
  {
    id: 'demo-expiry',
    group: 'Demo & claiming',
    question: "what happens if I don't claim it?",
    answer:
      "nothing bad — the demo simply expires after {demoTtlDays} days if no one claims it. there's no charge and no obligation to look.",
    surfaces: ['marketing_faq'],
  },
  {
    id: 'demo-real-site',
    group: 'Demo & claiming',
    question: 'is the demo a real site or just a preview?',
    // Merge: CLAIM_ANSWERS #1 was this answer with "simply" for "just".
    answer:
      "it's a real, working site at a real pageboss web address — not a screenshot. claiming it just makes it yours so you can edit, connect a domain, and publish.",
    surfaces: ['marketing_faq'],
  },
  {
    id: 'how-claiming-works',
    group: 'Demo & claiming',
    question: 'how does claiming work?',
    // Merge: CLAIM_ANSWERS #2 ("change anything" for "edit anything").
    answer:
      "sign in with Google or Apple, confirm it's your business, and the site is yours. from there you edit anything and pick a plan when you're ready to publish.",
    surfaces: ['marketing_faq'],
  },
  {
    id: 'change-everything',
    group: 'Demo & claiming',
    question: 'can I change everything?',
    // Merge: CLAIM_ANSWERS #4 was a paraphrase; FAQ_GROUPS wording survives.
    answer:
      'yes. text, photos, sections, colors, pages — all of it. the starting site is a head start, not a cage.',
    surfaces: ['marketing_faq', 'app_help'],
    screens: ['website', 'page-edit'],
  },
  {
    id: 'demo-is-your-trial',
    group: 'Demo & claiming',
    question: 'how does "the demo is your trial" work?',
    // Named dedupe: FAQ_GROUPS "is there a free trial?" + PRICING_QA #7 +
    // CLAIM_ANSWERS "what does it cost to look?" → the fullest (pricing) wording.
    answer:
      "there's no free trial because you already see your real site for free before you pay — the demo is your trial. when you're ready, claim it and pick a plan; if it's not for you, the {moneyBackDays}-day money-back guarantee has you covered.",
    surfaces: ['marketing_faq', 'marketing_pricing'],
  },
  // ── Domains & ownership ────────────────────────────────────────────────
  {
    id: 'own-my-site',
    group: 'Domains & ownership',
    question: 'do I own my website?',
    answer:
      'your content is always yours — take it with you whenever you leave, and we never hold your site hostage. every plan connects your own custom domain — or use your free pageboss web address (web.pageboss.com/yourname) if you’d rather.',
    surfaces: ['marketing_home', 'marketing_faq'],
  },
  {
    id: 'own-domain',
    group: 'Domains & ownership',
    question: 'can I use my own domain?',
    // Merge: PRICING_QA #4's fuller answer survives over FAQ_GROUPS'.
    answer:
      'yes. every plan connects (and keeps) your own domain; every paid plan includes 1 free domain. everything you write or upload stays yours, on every plan.',
    surfaces: ['marketing_faq', 'marketing_pricing', 'app_help'],
    screens: ['domain', 'website'],
  },
  {
    id: 'leave-with-everything',
    group: 'Domains & ownership',
    question: 'do I own my site and content if I leave?',
    // Merge: CLAIM_ANSWERS #5 was near-verbatim.
    answer:
      'yes. your content is yours. if you cancel, you can export your site and keep your domain — nothing is held hostage.',
    surfaces: ['marketing_faq', 'app_help'],
    screens: ['settings'],
  },
  {
    id: 'vs-wix',
    group: 'Domains & ownership',
    question: 'how is this different from Wix, Squarespace, or GoDaddy?',
    answer:
      "they hand you a blank builder and ignore Google. pageboss builds your site from your business info, helps you get found on Google, Maps, and AI search, and manages it all from one place — so you're not juggling tools.",
    surfaces: ['marketing_faq'],
  },
  {
    id: 'multi-location',
    group: 'Domains & ownership',
    question: 'can I run more than one location?',
    answer:
      'yes — on {growthPlanName}. group your locations and see map ranking, AI visibility, and competitors for every one of them on a single screen. your first {growthLocationsIncluded} locations are included; each extra location is {locationAddonMonthly}/mo and brings its own {locationExtraSearchTerms} search terms and {locationExtraCompetitors} competitors (your AI questions are shared across all your locations).',
    surfaces: ['marketing_faq', 'marketing_pricing', 'app_help'],
    screens: ['locations', 'billing'],
  },
  // ── Google & AI ──────────────────────────────────────────────────────────
  {
    id: 'ai-answers-meaning',
    group: 'Google & AI',
    question: 'what does "found in AI answers" mean?',
    answer:
      'more people are asking assistants like ChatGPT and Claude for local recommendations. assistants rely on verifiable facts — consistent details, structured data, real reviews. pageboss builds your presence so machines can read and trust it, the same way search engines do.',
    surfaces: ['marketing_home', 'marketing_faq', 'app_help'],
    screens: ['ai-visibility'],
  },
  {
    id: 'compliance',
    group: 'Google & AI',
    question: 'why do you talk about compliance so much?',
    answer:
      "because the biggest online risk to a local business isn't ranking a spot lower — it's getting suspended. Google actively removes profiles built on fake listings, doorway pages, or incentivized reviews, and texting customers without consent can violate federal law. we treat the rules as the product, not the obstacle.",
    surfaces: ['marketing_home', 'marketing_faq'],
  },
  {
    id: 'auto-post',
    group: 'Google & AI',
    question: 'will it post to Google automatically?',
    answer:
      "never. your assistant drafts replies and posts, but nothing goes out on its own. you read it, tweak it, and click 'approve and post.' that's the rule, not a limitation.",
    surfaces: ['marketing_faq', 'app_help'],
    screens: ['gbp', 'gbp-content', 'reviews'],
  },
  {
    id: 'ai-edit-first',
    group: 'Google & AI',
    question: 'how does the AI write replies and posts — can I edit first?',
    answer:
      "you'll always edit first. the assistant writes a draft in your voice, you change anything you want, then approve. nothing goes live until you say so.",
    surfaces: ['marketing_faq', 'app_help'],
    screens: ['gbp', 'gbp-content', 'reviews'],
  },
  // ── Tracking ─────────────────────────────────────────────────────────────
  {
    id: 'rank-guarantee',
    group: 'Tracking',
    question: 'will this get me to #1 on Google?',
    answer:
      "nobody can honestly promise that — where you rank locally depends heavily on how close the searcher is to you, which no tool controls. what pageboss does is make sure nothing about your presence is holding you back, and that you're eligible and easy to choose everywhere people look.",
    surfaces: ['marketing_home', 'marketing_faq', 'app_help'],
    screens: ['rank', 'aeo'],
  },
  {
    id: 'search-term-pack',
    group: 'Tracking',
    question: 'can I track more search terms than my plan includes?',
    answer:
      'yes — add a pack of {searchTermPackSize} more tracked search terms for {searchTermPackPrice}/mo, up to {searchTermPackMax} packs. your weekly map check picks them up automatically. ({starterPlanName} already tracks {proSearchTerms} search terms, {proCompetitors} competitors and {proAiQuestions} AI questions every week.)',
    surfaces: ['marketing_faq', 'app_help'],
    screens: ['rank', 'billing'],
  },
  // ── Plans & billing ──────────────────────────────────────────────────────
  {
    id: 'ai-pack',
    group: 'Plans & billing',
    question: 'what happens when I run out of AI helps?',
    answer:
      'no interruption — a {aiPackPrice} pack adds {aiPackSize} more AI helps whenever your plan runs low, and unused plan quota keeps refreshing monthly.',
    surfaces: ['marketing_faq', 'app_help'],
    screens: ['billing'],
  },
  {
    id: 'change-plans',
    group: 'Plans & billing',
    question: 'can I change plans later?',
    answer:
      'yes — pick a plan, change it anytime from your dashboard. upgrades take effect right away; downgrades at your next billing date. no calls, no fees.',
    surfaces: ['marketing_faq', 'marketing_pricing', 'app_help'],
    screens: ['billing'],
  },
  {
    id: 'what-is-ai-help',
    group: 'Plans & billing',
    question: "what's an AI help (generation)?",
    // Conflict 4 (flagged, wording kept): names Business/Growth allowances only.
    answer:
      'one help = one thing the assistant writes: a page of content, a block rewrite, a draft Google post, or a draft review reply. alt-text for your photos is free. {businessPlanName} includes {aiHelpsBusiness}/mo, {growthPlanName} {aiHelpsGrowth} — need more? add {aiPackSize} for {aiPackPrice}.',
    surfaces: ['marketing_faq', 'marketing_pricing', 'app_help'],
    screens: ['billing', 'page-edit'],
  },
  {
    id: 'annual-discount',
    group: 'Plans & billing',
    question: 'is annual really 2 months free?',
    answer:
      'yes — paying yearly costs about ten months instead of twelve. {starterPlanName} is {starterMonthly}/mo or {starterAnnual}/yr, and it scales the same way up the line. switch between monthly and annual whenever you want.',
    surfaces: ['marketing_faq', 'marketing_pricing', 'app_help'],
    screens: ['billing'],
  },
  {
    id: 'cancel-anytime',
    group: 'Plans & billing',
    question: 'is there a contract, or can I cancel anytime?',
    // Merge: PRICING_QA "what happens to my site if I cancel?" — fuller answer survives.
    answer:
      'your content is yours. cancel anytime: no contract, no cancellation fee. export your site and keep your domain. nothing is held hostage.',
    surfaces: ['marketing_faq', 'marketing_pricing', 'app_help'],
    screens: ['billing'],
  },
  {
    id: 'refund',
    group: 'Plans & billing',
    question: "is there a refund if it's not for me?",
    answer:
      "yes. tell us within {moneyBackDays} days and we'll refund you in full — no forms, no 'are you sure,' no hard feelings.",
    surfaces: ['marketing_faq', 'app_help'],
    screens: ['billing'],
  },
  {
    id: 'real-person',
    group: 'Plans & billing',
    question: 'is there a real person if I get stuck?',
    answer:
      'yes. email support is included on every plan, and {growthPlanName} adds priority support. a real person answers.',
    surfaces: ['marketing_faq', 'app_help'],
    screens: ['settings', 'billing'],
  },
];
